import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import VoiceNote from '../components/VoiceNote';
import PhotoUpload from '../components/PhotoUpload';
import ShareToRider from '../components/ShareToRider';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './DeliveryInstructions.css';

const DeliveryInstructions = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [instructions, setInstructions] = useState({
    voiceNote: null,
    photos: [],
    textInstructions: '',
    preferredLanguage: 'en'
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Load existing instructions
  useEffect(() => {
    const loadInstructions = async () => {
      if (!user?._id) return;

      setLoading(true);
      try {
        const response = await api.get('/delivery-instructions');
        if (response.data.success && response.data.data) {
          setInstructions(prev => ({
            ...prev,
            ...response.data.data
          }));
        }
      } catch (error) {
        console.log('No existing instructions found');
      } finally {
        setLoading(false);
      }
    };

    loadInstructions();
  }, [user]);

  // Handle voice note save
  const handleVoiceNoteSave = (voiceNoteData) => {
    setInstructions(prev => ({
      ...prev,
      voiceNote: voiceNoteData
    }));
    toast.success('Voice note saved!');
  };

  // Handle photos change
  const handlePhotosChange = (photos) => {
    setInstructions(prev => ({
      ...prev,
      photos
    }));
  };

  // Handle text instructions change
  const handleTextChange = (e) => {
    setInstructions(prev => ({
      ...prev,
      textInstructions: e.target.value
    }));
  };

  // Save all instructions
  const handleSaveAll = async () => {
    setSaving(true);
    try {
      // Convert photos to base64 for storage
      const photoData = instructions.photos.map(photo => ({
        src: photo.src,
        label: photo.label,
        caption: photo.caption
      }));

      const payload = {
        voiceNote: instructions.voiceNote ? {
          originalText: instructions.voiceNote.originalText,
          translatedText: instructions.voiceNote.translatedText,
          // Use translated text as the main text if available
          text: instructions.voiceNote.translatedText || instructions.voiceNote.originalText,
          sourceLang: instructions.voiceNote.sourceLang,
          targetLang: instructions.voiceNote.targetLang
        } : null,
        photos: photoData,
        textInstructions: instructions.textInstructions,
        preferredLanguage: instructions.preferredLanguage
      };

      const response = await api.post('/delivery-instructions', payload);

      if (response.data.success) {
        toast.success('Delivery instructions saved successfully!');
      }
    } catch (error) {
      console.error('Error saving instructions:', error);
      toast.error('Failed to save instructions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="delivery-instructions-page">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="delivery-instructions-page">
      <header className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>Delivery Instructions</h1>
      </header>

      <div className="instructions-container">
        {/* Voice Note Section */}
        <section className="instruction-section">
          <VoiceNote
            mode="record"
            onSave={handleVoiceNoteSave}
            originalLanguage={user?.preferredLanguage || 'en'}
          />
          {instructions.voiceNote && (
            <div className="saved-indicator">
              ✓ Voice note saved
            </div>
          )}
        </section>

        {/* Photo Upload Section */}
        <section className="instruction-section">
          <PhotoUpload
            onPhotosChange={handlePhotosChange}
            existingPhotos={instructions.photos}
            maxPhotos={5}
          />
        </section>

        {/* Text Instructions Section */}
        <section className="instruction-section text-section">
          <div className="text-instructions-container">
            <h3>📝 Additional Text Instructions</h3>
            <textarea
              placeholder="Add any additional instructions for the delivery person...&#10;&#10;Examples:&#10;• Ring the bell twice&#10;• Leave at the door if not available&#10;• Call before arriving&#10;• Gate code: 1234"
              value={instructions.textInstructions}
              onChange={handleTextChange}
              rows={6}
            />
          </div>
        </section>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button
            className="save-all-btn"
            onClick={handleSaveAll}
            disabled={saving}
          >
            {saving ? '⏳ Saving...' : '💾 Save All Instructions'}
          </button>

          <button
            className="share-btn"
            onClick={() => setShowShareModal(true)}
            disabled={!instructions.voiceNote && instructions.photos.length === 0 && !instructions.textInstructions}
          >
            📤 Share with Rider via WhatsApp
          </button>
        </div>

        {/* Preview Section */}
        <section className="preview-section">
          <h3>📋 What Delivery Person Will See</h3>
          <div className="preview-card">
            {instructions.voiceNote?.originalText && (
              <div className="preview-item">
                <span className="preview-label">🎤 Voice Instructions:</span>
                <p className="original-text">{instructions.voiceNote.originalText}</p>
                {instructions.voiceNote.translatedText && (
                  <>
                    <span className="preview-label translated-label">
                      🌐 Translated ({instructions.voiceNote.targetLang?.toUpperCase() || 'HI'}):
                    </span>
                    <p className="translated-text">{instructions.voiceNote.translatedText}</p>
                  </>
                )}
                {!instructions.voiceNote.translatedText && (
                  <small>
                    (Translate to your target language before saving)
                  </small>
                )}
              </div>
            )}

            {instructions.photos.length > 0 && (
              <div className="preview-item">
                <span className="preview-label">📷 Location Photos:</span>
                <div className="preview-photos">
                  {instructions.photos.map((photo, idx) => (
                    <img key={idx} src={photo.src} alt={photo.label} />
                  ))}
                </div>
              </div>
            )}

            {instructions.textInstructions && (
              <div className="preview-item">
                <span className="preview-label">📝 Text Instructions:</span>
                <p>{instructions.textInstructions}</p>
              </div>
            )}

            {!instructions.voiceNote?.originalText &&
              instructions.photos.length === 0 &&
              !instructions.textInstructions && (
                <div className="preview-empty">
                  No instructions added yet. Add voice notes, photos, or text
                  instructions above to help delivery persons find your location.
                </div>
              )}
          </div>
        </section>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <ShareToRider
          onClose={() => setShowShareModal(false)}
          onShareSuccess={(data) => {
            toast.success('Share link sent to rider!');
          }}
        />
      )}
    </div>
  );
};

export default DeliveryInstructions;
